import React, { useState, useEffect } from 'react';
import { Search, X, Check } from 'lucide-react';
import { BlogPost } from '../../types/api';
import { blogService } from '../../services/api';

// Função helper para normalizar tags
const normalizeTags = (tags: any): string[] => {
  if (Array.isArray(tags)) {
    return tags;
  }
  if (typeof tags === 'string') {
    try {
      // Tentar fazer parse JSON primeiro
      const parsed = JSON.parse(tags);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    } catch {
      // Se não conseguir fazer parse, tratar como string simples
      return tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
    }
  }
  return [];
};

interface RelatedPostsSelectorProps {
  selectedPosts: number[];
  onSelectionChange: (postIds: number[]) => void;
  excludePostId?: number; // Para excluir o post atual na edição
  maxSelections?: number;
}

const RelatedPostsSelector: React.FC<RelatedPostsSelectorProps> = ({
  selectedPosts,
  onSelectionChange,
  excludePostId,
  maxSelections = 2
}) => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Carregar posts
  useEffect(() => {
    const loadPosts = async () => {
      setLoading(true);
      try {
        const response = await blogService.getAll(1, 100); // Buscar até 100 posts
        if (response.success && response.data) {
          // Filtrar o post atual se estivermos editando
          const filtered = excludePostId 
            ? response.data.filter(post => post.id !== excludePostId)
            : response.data;
          setPosts(filtered);
          setFilteredPosts(filtered);
        }
      } catch (error) {
        console.error('Erro ao carregar posts:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      loadPosts();
    }
  }, [isOpen, excludePostId]);

  // Filtrar posts baseado na busca
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredPosts(posts);
    } else {
      const filtered = posts.filter(post => {
        const normalizedTags = normalizeTags(post.tags);
        return post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
               post.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
               normalizedTags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
      });
      setFilteredPosts(filtered);
    }
  }, [searchTerm, posts]);

  const handlePostToggle = (postId: number) => {
    if (selectedPosts.includes(postId)) {
      // Remover da seleção
      onSelectionChange(selectedPosts.filter(id => id !== postId));
    } else if (selectedPosts.length < maxSelections) {
      // Adicionar à seleção (se não exceder o limite)
      onSelectionChange([...selectedPosts, postId]);
    }
  };

  const removeSelectedPost = (postId: number) => {
    onSelectionChange(selectedPosts.filter(id => id !== postId));
  };

  const getSelectedPosts = () => {
    console.log('🔍 getSelectedPosts - selectedPosts:', selectedPosts);
    console.log('🔍 getSelectedPosts - posts:', posts.map(p => ({ id: p.id, title: p.title })));
    const filtered = posts.filter(post => selectedPosts.includes(post.id));
    console.log('🔍 getSelectedPosts - filtered:', filtered.map(p => ({ id: p.id, title: p.title })));
    return filtered;
  };

  return (
    <div className="space-y-4">
      {/* Posts selecionados */}
      {selectedPosts.length > 0 && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-brand-slate">
            Posts Relacionados Selecionados ({selectedPosts.length}/{maxSelections})
          </label>
          <div className="space-y-2">
            {getSelectedPosts().map(post => (
              <div
                key={post.id}
                className="flex items-center justify-between bg-brand-sand/30 rounded-lg p-3"
              >
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-brand-slate">{post.title}</h4>
                  <p className="text-xs text-brand-ink/60">{post.category}</p>
                </div>
                <button
                  type="button"
                  onClick={() => removeSelectedPost(post.id)}
                  className="text-brand-ink/40 hover:text-red-500 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Botão para abrir seletor */}
      <div>
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          disabled={selectedPosts.length >= maxSelections}
          className={`w-full px-4 py-2 border-2 border-dashed rounded-lg transition-colors ${
            selectedPosts.length >= maxSelections
              ? 'border-brand-ink/20 text-brand-ink/40 cursor-not-allowed'
              : 'border-brand-accent/50 text-brand-accent hover:border-brand-accent hover:bg-brand-accent/5'
          }`}
        >
          {selectedPosts.length >= maxSelections
            ? `Máximo de ${maxSelections} posts selecionados`
            : selectedPosts.length === 0
            ? 'Selecionar Posts Relacionados'
            : `Adicionar mais posts (${selectedPosts.length}/${maxSelections})`
          }
        </button>
      </div>

      {/* Modal de seleção */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-brand-slate/10">
              <div>
                <h3 className="text-lg font-semibold text-brand-slate">
                  Selecionar Posts Relacionados
                </h3>
                <p className="text-sm text-brand-ink/60">
                  Escolha até {maxSelections} posts relacionados
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="text-brand-ink/40 hover:text-brand-ink transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Busca */}
            <div className="p-6 border-b border-brand-slate/10">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-brand-ink/40 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Buscar posts por título, categoria ou tags..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-brand-slate/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent"
                />
              </div>
            </div>

            {/* Lista de posts */}
            <div className="flex-1 overflow-y-auto p-6">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-accent"></div>
                </div>
              ) : filteredPosts.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-brand-ink/60">
                    {searchTerm ? 'Nenhum post encontrado para a busca.' : 'Nenhum post disponível.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredPosts.map(post => {
                    const isSelected = selectedPosts.includes(post.id);
                    const canSelect = !isSelected && selectedPosts.length < maxSelections;
                    
                    return (
                      <div
                        key={post.id}
                        className={`p-4 rounded-lg border transition-all cursor-pointer ${
                          isSelected
                            ? 'border-brand-accent bg-brand-accent/5'
                            : canSelect
                            ? 'border-brand-slate/20 hover:border-brand-accent/50 hover:bg-brand-accent/5'
                            : 'border-brand-slate/10 bg-brand-slate/5 cursor-not-allowed'
                        }`}
                        onClick={() => canSelect && handlePostToggle(post.id)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium text-brand-slate mb-1">{post.title}</h4>
                            <div className="flex items-center gap-4 text-xs text-brand-ink/60">
                              <span>{post.category}</span>
                              <span>{post.read_time}</span>
                              <span>{new Date(post.date).toLocaleDateString('pt-BR')}</span>
                            </div>
                            {(() => {
                              const normalizedTags = normalizeTags(post.tags);
                              return normalizedTags.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {normalizedTags.slice(0, 3).map(tag => (
                                    <span
                                      key={tag}
                                      className="px-2 py-1 bg-brand-sand/30 text-xs text-brand-ink/70 rounded"
                                    >
                                      {tag}
                                    </span>
                                  ))}
                                  {normalizedTags.length > 3 && (
                                    <span className="text-xs text-brand-ink/50">
                                      +{normalizedTags.length - 3} mais
                                    </span>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                          <div className="ml-4">
                            {isSelected ? (
                              <div className="w-6 h-6 bg-brand-accent rounded-full flex items-center justify-center">
                                <Check className="w-4 h-4 text-white" />
                              </div>
                            ) : canSelect ? (
                              <div className="w-6 h-6 border-2 border-brand-slate/30 rounded-full"></div>
                            ) : (
                              <div className="w-6 h-6 border-2 border-brand-slate/10 rounded-full bg-brand-slate/10"></div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-brand-slate/10">
              <div className="flex items-center justify-between">
                <p className="text-sm text-brand-ink/60">
                  {selectedPosts.length} de {maxSelections} posts selecionados
                </p>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="btn-primary"
                >
                  Concluir Seleção
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RelatedPostsSelector;
